import { forwardRef, RefObject, useEffect } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  DropdownInput,
  CrossOutline,
  DatePickerInput,
  ArrowRightOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import { useCreatePayCalendarStore } from 'app/store';

import { createPayCalendarCutoffInformationFormSchema } from 'app/utils/schemas';

import dateUtils from 'app/utils/date';

import {
  getCutOffOptions,
  getNonBusinessDayFailoverOptions,
} from 'app/components/PayCalendars/options';

import {
  PayCalendarCutoffInformationFormFields,
  PayCalendarFormProps,
  PayCalendarDefineCalendarFormFields,
  PayCalendarPaydayInformationFormFields,
} from '../types';

const PayCalendarCutoffInformationForm = forwardRef<HTMLElement, PayCalendarFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
    const setPayCalendarData = useCreatePayCalendarStore((state) => state.setPayCalendarData);

    const { payFrequency } = payCalendarData as PayCalendarDefineCalendarFormFields; // pay frequency determines the cut off options

    const {
      startDate,
      payday,
      paydayLastCustomWeekday,
      paydayFixedDay,
      paydayNDayOfPeriod,
      paydayNBusinessDayOfPeriod,
    } = payCalendarData as PayCalendarPaydayInformationFormFields;

    const firstPayDate = dateUtils.getFirstPayDate(
      payday,
      payFrequency,
      startDate,
      paydayLastCustomWeekday,
      paydayFixedDay,
      paydayNDayOfPeriod,
      paydayNBusinessDayOfPeriod
    );

    const {
      register,
      control,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      trigger,
      formState: { errors, isValid },
    } = useForm<PayCalendarCutoffInformationFormFields>({
      mode: 'onChange',
      defaultValues: payCalendarData as PayCalendarCutoffInformationFormFields,
      resolver: yupResolver(
        createPayCalendarCutoffInformationFormSchema(
          payday,
          paydayNDayOfPeriod,
          paydayNBusinessDayOfPeriod
        )
      ),
    });

    // watchers
    const watchPayrollCutoff = watch('payrollCutoff');

    useEffect(() => {
      trigger('payrollCutoffDays');
    }, [watchPayrollCutoff, trigger]);

    // populate options fields
    const cutoffOptions = getCutOffOptions(payFrequency);
    const cutOffNonBusinessDayFailoverOptions = getNonBusinessDayFailoverOptions();

    const resetAndFocusField = (field: keyof PayCalendarCutoffInformationFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const handleNextStep = (data: PayCalendarCutoffInformationFormFields) => {
      setPayCalendarData(data);
      onStepAction();
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setPayCalendarData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          {/*  Cut-off Type */}
          <FormControl isInvalid={!!errors?.payrollCutoff?.message} isRequired>
            <FormLabel>Cut-off type</FormLabel>
            <Controller
              control={control}
              name="payrollCutoff"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a cut-off type"
                  defaultValue={cutoffOptions.find((type) => type.value === value)}
                  options={cutoffOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.payrollCutoff?.message}</FormErrorMessage>
          </FormControl>

          {/*  Optional Fields: Number of days before payday, Number of business days before payday, Last Custom Weekday, Nth Business Day of Period, Nth Day of Period, Fixed Day */}

          {watchPayrollCutoff !== undefined && watchPayrollCutoff === 'N_DAYS_BEFORE_PAYDAY' && (
            <FormControl isInvalid={!!errors?.payrollCutoffDays?.message} isRequired>
              <FormLabel>How many days before payday?</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder="Enter the day of the period"
                  {...register('payrollCutoffDays')}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Clear days before payday"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('payrollCutoffDays')}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors?.payrollCutoffDays?.message}</FormErrorMessage>
            </FormControl>
          )}

          {watchPayrollCutoff !== undefined &&
            watchPayrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' && (
              <FormControl isInvalid={!!errors?.payrollCutoffDays?.message} isRequired>
                <FormLabel>How many business days before payday?</FormLabel>
                <InputGroup>
                  <Input
                    type="number"
                    placeholder="Enter the day of the period"
                    {...register('payrollCutoffDays')}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear days before payday"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('payrollCutoffDays')}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors?.payrollCutoffDays?.message}</FormErrorMessage>
              </FormControl>
            )}

          {watchPayrollCutoff !== undefined && watchPayrollCutoff === 'N_DAY_OF_PERIOD' && (
            <FormControl isInvalid={!!errors?.payrollCutoffDays?.message} isRequired>
              <FormLabel>What day of the period</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder="Enter the day of the period"
                  {...register('payrollCutoffDays')}
                />
                {watchPayrollCutoff === 'N_DAY_OF_PERIOD' && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear payday custom day"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('payrollCutoffDays')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.payrollCutoffDays?.message}</FormErrorMessage>
            </FormControl>
          )}
          {watchPayrollCutoff !== undefined &&
            watchPayrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD' && (
              <FormControl isInvalid={!!errors?.payrollCutoffDays?.message} isRequired>
                <FormLabel>What business day of the period</FormLabel>
                <InputGroup>
                  <Input
                    type="number"
                    placeholder="Enter the day of the period"
                    {...register('payrollCutoffDays')}
                  />
                  {watchPayrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD' && (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear payday custom day"
                        icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                        variant="link"
                        onClick={() => resetAndFocusField('payrollCutoffDays')}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
                <FormErrorMessage>{errors?.payrollCutoffDays?.message}</FormErrorMessage>
              </FormControl>
            )}
          {watchPayrollCutoff !== undefined && watchPayrollCutoff === 'FIXED_DAY' && (
            <FormControl isInvalid={!!errors?.payrollCutoffFixedDay?.message} isRequired>
              <FormLabel>Fixed day</FormLabel>
              <Controller
                control={control}
                name="payrollCutoffFixedDay"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DatePickerInput
                    placeholder="What fixed day do you use?"
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    firstPayDay={firstPayDate}
                  />
                )}
              />
              <FormErrorMessage>{errors?.payrollCutoffFixedDay?.message}</FormErrorMessage>
            </FormControl>
          )}

          {/*  Default Cut Off Action */}

          <FormControl isInvalid={!!errors?.cutOffNonBusinessDayFailover?.message} isRequired>
            <FormLabel>Default cut-off action</FormLabel>
            <Controller
              control={control}
              name="cutOffNonBusinessDayFailover"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="If payday lands on a weekend or bank holiday"
                  defaultValue={cutOffNonBusinessDayFailoverOptions.find(
                    (cutOffNonBusinessDayFailover) => cutOffNonBusinessDayFailover.value === value
                  )}
                  options={cutOffNonBusinessDayFailoverOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.cutOffNonBusinessDayFailover?.message}</FormErrorMessage>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

PayCalendarCutoffInformationForm.displayName = 'PayCalendarCutoffForm';

export default PayCalendarCutoffInformationForm;
